import { showsTabIconsFromScale, tileSizeFromScale } from "@/stores/v2/classroomLayoutStore";

export type Slot = string | null; // student qoriaUserId, or null for an empty seat

export interface SeatLayout {
	slots: Slot[]; // length === columns * rows
	columns: number;
	rows: number;
	cardWidth: number;
}

/** `"custom"` means the seats were arranged by hand rather than by a name sort. */
export type SeatLayoutSortOrder = "custom" | "lastNameAscending";

export interface SeatRosterStudent {
	qoriaUserId?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	username?: string | null;
	pinned?: boolean | null;
}

export const DEFAULT_COLUMNS = 6;

export const MIN_COLUMNS = 4;
export const MAX_COLUMNS = 10;
export const MIN_ROWS = 1;
export const MAX_ROWS = 10;
export const MIN_CARD_WIDTH = 150;
export const MAX_CARD_WIDTH = 350;
export const CARD_WIDTH_STEP = 1;
export const DEFAULT_CARD_WIDTH = Math.round((MIN_CARD_WIDTH + MAX_CARD_WIDTH) / 2);

const CARD_HEIGHT_AT_SCALE_0 = 200;
const CARD_HEIGHT_AT_SCALE_HALF = 301;
const CARD_HEIGHT_AT_SCALE_1 = 391;

// The default-view calibration anchors above include the per-student action toolbar
// row that `RegularClassroomUser` renders inside `StudentTileHeader` (chat / open-tab /
// pause / focus buttons, fixed 30px tall). Monitor-only tiles (`MonitoringClassroomUser`)
// omit that row, so their cards are this much shorter. Seat plans rendering action-less
// tiles subtract it so empty/loading placeholders match the (shorter) filled monitor cards
// instead of stretching every tile to the taller regular-tile height.
export const ACTION_ROW_HEIGHT_PX = 30;

const QUAD_A = 2 * (CARD_HEIGHT_AT_SCALE_0 + CARD_HEIGHT_AT_SCALE_1 - 2 * CARD_HEIGHT_AT_SCALE_HALF);
const QUAD_B = CARD_HEIGHT_AT_SCALE_1 - CARD_HEIGHT_AT_SCALE_0 - QUAD_A;
const QUAD_C = CARD_HEIGHT_AT_SCALE_0;

function clamp01(n: number): number {
	if (Number.isNaN(n)) return 0;
	if (n < 0) return 0;
	if (n > 1) return 1;
	return n;
}

export function tileScaleFromCardWidth(cardWidth: number): number {
	return clamp01((cardWidth - MIN_CARD_WIDTH) / (MAX_CARD_WIDTH - MIN_CARD_WIDTH));
}

export function deriveCardHeight(cardWidth: number, hasActionRow = true): number {
	const scale = tileScaleFromCardWidth(cardWidth);
	const base = Math.round(QUAD_A * scale * scale + QUAD_B * scale + QUAD_C);
	return hasActionRow ? base : base - ACTION_ROW_HEIGHT_PX;
}

// Natural height of a filled tile with an active screenshot, used as the
// min-height for placeholder / inactive seats so tiles don't jump when
// the first screenshot arrives. Constants mirror `StudentTile.scss` /
// `RegularClassroomUser.scss` (including the `_grid_small` / `_grid_noTabIcons`
// slider-bucket overrides).
const HEADER_BASE_MD_LG = 57;
const HEADER_BASE_SM = 46;
const TILE_PADDING_Y = 4;
const CONTENT_BORDER_PX = 1;
const SCREEN_CONTENT_PADDING_Y = 16;
const SCREEN_HEADER_PX = 25;
const TABS_ROW_MIN_PX = 30;
const SCREEN_CONTENT_HORIZONTAL_INSET = 24;

export function deriveActiveTileMinHeightPx(cardWidth: number, hasActionRow = true): number {
	const scale = tileScaleFromCardWidth(cardWidth);
	const screenshotWidth = Math.max(0, cardWidth - SCREEN_CONTENT_HORIZONTAL_INSET);
	const screenshotHeight = Math.round((screenshotWidth * 9) / 16);

	const headerBase = tileSizeFromScale(scale) === "sm" ? HEADER_BASE_SM : HEADER_BASE_MD_LG;
	const header = headerBase + (hasActionRow ? ACTION_ROW_HEIGHT_PX : 0);

	const tabsRowHeight = showsTabIconsFromScale(scale) ? TABS_ROW_MIN_PX : 0;

	return header + TILE_PADDING_Y + CONTENT_BORDER_PX + SCREEN_CONTENT_PADDING_Y + SCREEN_HEADER_PX + screenshotHeight + tabsRowHeight;
}

export function defaultRows(count: number, columns: number): number {
	return Math.max(MIN_ROWS, Math.ceil(count / columns));
}

/**
 * Auto-sorted seating: one seat per student and nothing else, so the grid reads like the default
 * view. Empty seats belong to a hand-arranged layout, so they only appear once a teacher drags.
 */
export function buildDefaultLayout(
	rosterIds: string[],
	columns: number = DEFAULT_COLUMNS,
	cardWidth: number = DEFAULT_CARD_WIDTH
): SeatLayout {
	const cols = Math.max(1, columns);
	return { slots: [...rosterIds], columns: cols, rows: defaultRows(rosterIds.length, cols), cardWidth };
}

/** Drop the empty seats from a grid so only seated students remain. */
export function compactSlots(slots: Slot[], columns: number): { slots: Slot[]; rows: number } {
	const seated = slots.filter((s): s is string => s !== null);
	return { slots: seated, rows: defaultRows(seated.length, Math.max(1, columns)) };
}

/** Pad a grid back out to `columns * rows` so every cell is a drop target. */
export function expandSlots(slots: Slot[], columns: number, rows: number): { slots: Slot[]; rows: number } {
	const cols = Math.max(1, columns);
	const required = Math.max(rows, defaultRows(slots.length, cols));
	return { slots: resizeSlots(slots, cols, required), rows: required };
}

export function placeOverflow(slots: Slot[], overflow: string[]): Slot[] {
	const next = [...slots];
	let cursor = 0;
	for (const id of overflow) {
		while (cursor < next.length && next[cursor] !== null) cursor++;
		if (cursor >= next.length) break;
		next[cursor] = id;
		cursor++;
	}
	return next;
}

export function resizeSlots(slots: Slot[], columns: number, rows: number): Slot[] {
	const capacity = columns * rows;
	const kept = slots.slice(0, capacity);
	const overflow = slots.slice(capacity).filter((s): s is string => s !== null);
	const padded: Slot[] = [...kept, ...new Array(Math.max(0, capacity - kept.length)).fill(null)];
	return placeOverflow(padded, overflow);
}

export function reconcileLayout(layout: SeatLayout, rosterIds: string[]): SeatLayout {
	const roster = new Set(rosterIds);
	const cleaned: Slot[] = layout.slots.map((s) => (s !== null && roster.has(s) ? s : null));

	const placed = new Set(cleaned.filter((s): s is string => s !== null));
	const newcomers = rosterIds.filter((id) => !placed.has(id));

	const freeCount = cleaned.filter((s) => s === null).length;
	let rows = layout.rows;
	let slots = cleaned;

	if (newcomers.length > freeCount) {
		const needed = newcomers.length - freeCount;
		const extraRows = Math.ceil(needed / layout.columns);
		rows += extraRows;
		slots = [...cleaned, ...new Array(extraRows * layout.columns).fill(null)];
	}

	slots = placeOverflow(slots, newcomers);
	return { ...layout, slots, rows };
}

export function swapSlots(slots: Slot[], from: number, to: number): Slot[] {
	if (from < 0 || to < 0 || from >= slots.length || to >= slots.length) return slots;
	const next = [...slots];
	const tmp = next[from];
	next[from] = next[to];
	next[to] = tmp;
	return next;
}

/** Chakra `gap="1rem"` on the seat grid. */
export const SEAT_GRID_GAP_PX = 16;

/** Horizontal padding on the seat-plan scroll container (`24px` each side). */
export const SEAT_GRID_PADDING_X_PX = 24;

export function columnsFromAvailableWidth(availableWidth: number, cardWidth: number, gap: number = SEAT_GRID_GAP_PX): number {
	if (cardWidth <= 0 || availableWidth <= 0) return 1;
	return Math.max(1, Math.floor((availableWidth + gap) / (cardWidth + gap)));
}

/** Keep the 1D seat sequence (including holes) and pad so length === columns * rows. */
export function reflowSlots(slots: Slot[], columns: number): Slot[] {
	const cols = Math.max(1, columns);
	const rows = Math.max(MIN_ROWS, Math.ceil(slots.length / cols) || 1);
	return resizeSlots(slots, cols, rows);
}

export function appendEmptyRow(slots: Slot[], columns: number): Slot[] {
	return [...slots, ...new Array(Math.max(1, columns)).fill(null)];
}

/**
 * Drop onto an existing seat, or onto a ghost cell past the last slot (`to >= slots.length`)
 * which appends a row and places the student in that column.
 */
export function dropOntoSeat(slots: Slot[], columns: number, from: number, to: number): { slots: Slot[]; rows: number } {
	const cols = Math.max(1, columns);
	if (from < 0 || from >= slots.length) {
		return { slots, rows: Math.max(MIN_ROWS, Math.ceil(slots.length / cols) || 1) };
	}
	if (from === to) {
		return { slots, rows: Math.max(MIN_ROWS, Math.ceil(slots.length / cols) || 1) };
	}

	let next = [...slots];
	let dest = to;
	if (to >= next.length) {
		const col = Math.max(0, Math.min(cols - 1, to - next.length));
		next = appendEmptyRow(next, cols);
		dest = slots.length + col;
	}

	next = swapSlots(next, from, dest);
	const rows = Math.max(MIN_ROWS, Math.ceil(next.length / cols) || 1);
	next = resizeSlots(next, cols, rows);
	return { slots: next, rows };
}

function nameKey(student: SeatRosterStudent, field: "firstName" | "lastName"): string {
	return (student[field] ?? "").trim().toLowerCase();
}

type PlaceableStudent<T extends SeatRosterStudent = SeatRosterStudent> = T & { qoriaUserId: string };

/**
 * Activity-then-name order, shared by the grid seats and the pinned strip.
 *
 * `activityOrderedIds` is the live activity order the classroom already computes for its tiles
 * (students with screenshots first, then connecting, then timed out). Callers that follow it
 * re-order as it changes, so tiles match the classroom order at all times. Ties inside a group —
 * and any roster member the activity order does not mention yet, which is all the GetClass
 * hydration has before the first tiles arrive — fall back to last name, then first name, then
 * username. The persisted `studentSortOrder` from personalisation is ignored either way.
 *
 * Students without a `qoriaUserId` are dropped: there is nothing to seat or key them by.
 */
export function sortSeatRosterStudents<T extends SeatRosterStudent>(
	students: T[],
	activityOrderedIds?: readonly string[]
): PlaceableStudent<T>[] {
	const activityRank = new Map<string, number>();
	activityOrderedIds?.forEach((id, index) => {
		if (!activityRank.has(id)) activityRank.set(id, index);
	});
	const unranked = activityRank.size;

	return students
		.filter((s): s is PlaceableStudent<T> => typeof s.qoriaUserId === "string" && s.qoriaUserId.length > 0)
		.sort((a, b) => {
			const rankCompared = (activityRank.get(a.qoriaUserId) ?? unranked) - (activityRank.get(b.qoriaUserId) ?? unranked);
			if (rankCompared !== 0) return rankCompared;
			const primaryCompared = nameKey(a, "lastName").localeCompare(nameKey(b, "lastName"));
			if (primaryCompared !== 0) return primaryCompared;
			const secondaryCompared = nameKey(a, "firstName").localeCompare(nameKey(b, "firstName"));
			if (secondaryCompared !== 0) return secondaryCompared;
			return (a.username ?? a.qoriaUserId).localeCompare(b.username ?? b.qoriaUserId);
		});
}

/**
 * Seat order for an auto-sorted grid.
 *
 * Pinned students are excluded: they are drawn in the pinned strip above the grid, never seated.
 * Leaving them in would let the roster reconcile drop them back into an empty seat after the
 * pinned sync has vacated theirs.
 *
 * Custom layouts do not use this: their slots are already arranged by the teacher.
 */
export function sortSeatRosterIds(students: SeatRosterStudent[], activityOrderedIds?: readonly string[]): string[] {
	return sortSeatRosterStudents(
		students.filter((s) => !s.pinned),
		activityOrderedIds
	).map((s) => s.qoriaUserId);
}
